import React, {useEffect} from 'react';
import {Autocomplete, CircularProgress, createFilterOptions, InputLabel, Stack, TextField} from '@mui/material';
import {Observable} from 'rxjs';

import {SelectItem} from '@components/inputs/select/Select.component';
import {ArrowDown} from '@icons/index';

interface AsyncSelectComponentProps {
    color?: 'primary' | 'secondary';
    disabled?: boolean;
    error?: string;
    getOptions: () => Observable<SelectItem[]>;
    handleChange?: (values: any) => void;
    label?: string;
    labelWidth?: number;
    placeholder?: string;
    readOnly?: boolean;
    required?: boolean;
    value?: any;
}

const AsyncSelectComponent = (props: AsyncSelectComponentProps) => {
    const {
        color = 'primary',
        disabled,
        error,
        getOptions,
        handleChange,
        label,
        labelWidth,
        placeholder,
        readOnly,
        required,
        value,
    } = props;

    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<readonly any[]>([]);
    const loading = !readOnly && open && options.length === 0;

    useEffect(() => {
        let active = true;

        if (!loading) return undefined;

        getOptions().subscribe((o) => {
            if (active) setOptions(o);
        });

        return () => {
            active = false;
        };
    }, [getOptions, loading]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      {!!label && (
        <InputLabel error={!!error} sx={{width: labelWidth ? `${labelWidth}px` : undefined}}>
          {label + (required ? '*' : '')}
        </InputLabel>
      )}
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        getOptionLabel={(option: SelectItem) => option.label}
        isOptionEqualToValue={(option: SelectItem, value: any) => option.value === value.value}
        options={options}
        loading={loading}
        value={value}
        readOnly={readOnly}
        filterOptions={createFilterOptions({matchFrom: 'any', stringify: (option) => option.label})}
        onChange={(evt, values) => handleChange?.(values)}
        popupIcon={!readOnly ? <ArrowDown/> : null}
        sx={{
          '& .MuiAutocomplete-input': {
            padding : '9px 32px 14px 4px !important'
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            color={color}
            focused={readOnly ? false : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20}/> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              placeholder: !value && !readOnly ? placeholder : undefined,
            }}
            error={!!error}
            helperText={error}/>
        )}/>
    </Stack>
  );
};

export default AsyncSelectComponent;
